import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Shield, ExternalLink, Loader2 } from "lucide-react";

/**
 * TwoCheckoutModal — embeds the lender's hosted checkout in an in-app iframe.
 *
 * Props:
 *   open          : boolean — controls visibility
 *   onClose       : () => void
 *   paymentUrl    : string — the lender payment_url returned from /api/two/*
 *   titleAmount   : string — e.g. "$540.00" — shown in the header for context
 *   onSuccess     : (ctx: {invoice?: string}) => void — fires when buyer finishes
 *   onCancel      : (ctx: {invoice?: string}) => void — fires if buyer cancels
 *
 * Completion detection:
 *   the lender redirects the iframe to `{REACT_APP_BACKEND_URL}/two/done?...` on
 *   finish. That route renders TwoDoneBridge, which posts a `verqo:two-done`
 *   message to this window. We listen for it here.
 */
export default function TwoCheckoutModal({
  open,
  onClose,
  paymentUrl,
  titleAmount,
  onSuccess,
  onCancel,
}) {
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);

  // Listen for completion message from TwoDoneBridge
  useEffect(() => {
    if (!open) return undefined;
    const handler = (ev) => {
      const d = ev?.data;
      if (!d || d.type !== "verqo:two-done") return;
      if (d.status === "cancel") {
        onCancel?.({ invoice: d.invoice });
      } else {
        onSuccess?.({ invoice: d.invoice });
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [open, onSuccess, onCancel]);

  // Reset loading state whenever the URL changes
  useEffect(() => {
    if (open) setLoading(true);
  }, [paymentUrl, open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="two-checkout-modal">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-border">
          <DialogTitle className="display text-2xl tracking-tight flex items-center gap-2">
            <Shield className="w-5 h-5 text-foreground/60" />
            Authorize
          </DialogTitle>
          <div className="text-xs text-foreground/55 mt-0.5 flex items-center gap-3">
            {titleAmount && <span className="display">{titleAmount}</span>}
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              the lender secure checkout
            </span>
          </div>
        </DialogHeader>

        <div className="relative bg-white" style={{ height: "70vh" }}>
          {loading && (
            <div className="absolute inset-0 grid place-items-center bg-background/80 z-10" data-testid="two-modal-loading">
              <div className="flex items-center gap-2 text-foreground/55 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading secure the lender checkout…
              </div>
            </div>
          )}
          {paymentUrl ? (
            <iframe
              ref={iframeRef}
              src={paymentUrl}
              onLoad={() => setLoading(false)}
              title="the lender checkout"
              className="w-full h-full border-0"
              /* Allow the the lender hosted page to run its own JS, make requests,
                 and submit forms; restrict everything else. */
              sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
              data-testid="two-checkout-iframe"
            />
          ) : (
            <div className="h-full grid place-items-center text-foreground/50 text-sm">
              No payment URL provided.
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-border flex items-center justify-between text-[11px] text-foreground/50">
          <span>Secure · SSL · PCI L1</span>
          <a
            href={paymentUrl || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 hover:text-foreground"
            data-testid="two-open-external"
          >
            Having trouble? Open in new tab <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
